import { z } from 'zod';

import { AirServerError, copyProperties, Summary, SummaryData, SummaryLike } from '@airreading/common';
import { entityTypeSchema } from './association.js';

export const v1IdSchema = z.string().brand<'v1Id'>();
export const V1Id = v1IdSchema.parse.bind(v1IdSchema);

/**
 * A V1 ID type that is distinct from Airtable record IDs. These usually
 * represent some kind of formula field inside of an Airtable view that is
 * intended to be unique.
 */
export type V1Id = z.infer<typeof v1IdSchema>;

export const v1CoachImportSuccessSchema = z.object({
  recordId: z.string(),
  tableName: z.string(),
});
export type V1CoachImportSuccess = z.infer<typeof v1CoachImportSuccessSchema>;

export const v1CoachImportFailureSchema = z.object({
  coachFullName: z.string(),
  email: z.string(),
  tableName: z.string(),
  recordId: z.string(),
  error: z.unknown(),
});
export type V1CoachImportFailure = z.infer<typeof v1CoachImportFailureSchema>;

export class V1CoachImportSummary extends Summary<V1CoachImportSuccess, V1CoachImportFailure> {}
export type V1CoachImportSummaryLike = SummaryLike<V1CoachImportSuccess, V1CoachImportFailure>;

export const v1ImportEntityTypeSchema = z.enum([...entityTypeSchema.options, 'student']);
export type V1ImportEntityType = z.infer<typeof v1ImportEntityTypeSchema>;

export const v1ImportKindSchema = z.enum(['create', 'update', 'delete']);
export type V1ImportKind = z.infer<typeof v1ImportKindSchema>;

export const v1OrgChartImportSuccessSchema = z.object({
  type: v1ImportEntityTypeSchema,
  kind: v1ImportKindSchema,
  internalId: z.string().optional(),
  externalId: z.string().optional(),
});
export type V1OrgChartImportSuccess = z.infer<typeof v1OrgChartImportSuccessSchema>;

export const v1OrgChartImportFailureSchema = z.object({
  type: v1ImportEntityTypeSchema,
  kind: v1ImportKindSchema,
  externalId: z.string().optional(),
  internalId: z.string().optional(),
  error: z.instanceof(Error),
  delta: z.unknown().optional(),
});
export type V1OrgChartImportFailure = z.infer<typeof v1OrgChartImportFailureSchema>;

export const v1OrgChartImportSkipSchema = z.object({
  type: v1ImportEntityTypeSchema,
  externalId: z.string().optional(),
  internalId: z.string().optional(),
});
export type V1OrgChartImportSkip = z.infer<typeof v1OrgChartImportSkipSchema>;

export type V1OrgChartImportSummaryData = SummaryData<V1OrgChartImportSuccess, V1CoachImportFailure> & {
  skips: V1OrgChartImportSkip[];
};

export class V1OrgChartImportSummary extends Summary<
  V1OrgChartImportSuccess,
  V1OrgChartImportFailure,
  V1OrgChartImportSkip
> {}
export type V1OrgChartImportSummaryLike = SummaryLike<
  V1OrgChartImportSuccess,
  V1OrgChartImportFailure,
  V1OrgChartImportSkip
>;

export const v1ScheduleImportFailureSchema = z.object({
  schedule: z.string(),
  error: z.instanceof(AirServerError),
});
export type V1ScheduleImportFailure = z.infer<typeof v1ScheduleImportFailureSchema>;

export const v1ScheduleImportSkipSchema = z.object({
  schedule: z.string(),
  reason: z.string(),
});
export type V1ScheduleImportSkip = z.infer<typeof v1ScheduleImportSkipSchema>;

export class V1ScheduleImportSummary extends Summary<string, V1ScheduleImportFailure, V1ScheduleImportSkip> {
  static override fromJSON<T = Summary<string, V1ScheduleImportFailure, V1ScheduleImportSkip>>(data: any): T {
    return copyProperties(new this() as any, data, this.summaryDataProperties);
  }
}
export type V1ScheduleImportSummaryLike = SummaryLike<string, V1ScheduleImportFailure, V1ScheduleImportSkip>;

export const v1StudentGroupImportFailureSchema = z.object({
  studentGroup: z.string(),
  error: z.instanceof(AirServerError),
});
export type V1StudentGroupImportFailure = z.infer<typeof v1StudentGroupImportFailureSchema>;

const v1ImportableStudentGroupSkipSchema = z.object({
  section: z.string().optional(),
  reason: z.string(),
  recordId: z.string().optional(),
});

export type V1ImportableStudentGroupSkip = z.infer<typeof v1ImportableStudentGroupSkipSchema>;

export class V1StudentGroupImportSummary extends Summary<
  string,
  V1StudentGroupImportFailure,
  V1ImportableStudentGroupSkip
> {
  static override fromJSON<T = Summary<string, V1StudentGroupImportFailure, V1ImportableStudentGroupSkip>>(
    data: any,
  ): T {
    return copyProperties(new this() as any, data, this.summaryDataProperties);
  }
}
export type V1StudentGroupImportSummaryLike = SummaryLike<
  string,
  V1StudentGroupImportFailure,
  V1ImportableStudentGroupSkip
>;
